/**
 * TanStack Query primitives for shift data.
 *
 * This module hosts the shared query-key factory and the invalidation helper
 * used by the shift data layer. The actual fetching hook lives in
 * `useShiftsRangeQuery.ts` — see that file for the range-based query that
 * replaced the legacy `useShiftsQuery` / `useMonthShiftsQuery` / `useDayShiftsQuery`
 * / `useTodayShiftsQuery` combo.
 */

import { useQueryClient } from '@tanstack/react-query';
import { DayShift } from '@/types';
import { UnifiedShiftQueryResult } from '@/lib/scheduling/unified-shift-service';

// Query key factory for consistent cache keys
//
// IMPORTANT: All list-shaped keys MUST be prefixed with `shiftQueryKeys.lists()`
// so that prefix-based invalidation in `useShiftMutations.ts` continues to match.
// Mutation code destructures `[, , businessId, startDate, endDate]` from keys,
// so positions 2, 3, 4 must remain (businessId, startIso, endIso) for the
// range(...) shape.
export const shiftQueryKeys = {
  all: ['shifts'] as const,
  lists: () => [...shiftQueryKeys.all, 'list'] as const,
  /**
   * Range-based cache key including all query inputs that affect the result.
   * `employeeIdsKey` should be a deterministic (sorted + joined) string so that
   * equivalent employee lists share a cache entry.
   */
  range: (
    businessId: string,
    startIso: string,
    endIso: string,
    hidePastDays: boolean,
    employeeIdsKey: string
  ) =>
    [
      ...shiftQueryKeys.lists(),
      businessId,
      startIso,
      endIso,
      hidePastDays,
      employeeIdsKey,
    ] as const,
  day: (businessId: string, date: string) =>
    [...shiftQueryKeys.all, 'day', businessId, date] as const,
};

export interface UseShiftsQueryResult {
  shifts: DayShift[];
  isLoading: boolean;
  isFetching: boolean;
  isRefetching: boolean;
  error: Error | null;
  refetch: () => void;
  performanceStats: UnifiedShiftQueryResult['performanceStats'] | null;
}

/**
 * Hook to invalidate shifts cache.
 * Use this after mutations to trigger background refetch.
 */
export function useInvalidateShifts() {
  const queryClient = useQueryClient();

  return {
    // Invalidate all shifts for a business
    invalidateAll: (businessId: string) => {
      queryClient.invalidateQueries({
        queryKey: [...shiftQueryKeys.all],
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key.includes(businessId);
        },
      });
    },
    // Invalidate all shifts queries (nuclear option)
    invalidateAllShifts: () => {
      queryClient.invalidateQueries({
        queryKey: shiftQueryKeys.all,
      });
    },
  };
}
